import { NotificationService } from 'src/app/shared/services/notification.service';
import { inject, Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { Menu } from 'primeng/menu';

import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { BaseServices } from '../../kuba.services';
import {
  complianceReportFileName,
  FlexiComplianceService,
  isFlexiComplianceAuthor,
} from './flexi-compliance.service';
import {
  FlexiComplianceScorePoint,
  FlexiComplianceStatus,
  FlexiComplianceSummary,
  isFlexiComplianceSnapshot,
} from './flexi-compliance.models';
import { FlexiComplianceTemplateService } from './templates/flexi-compliance-template.service';

/** One row of the template picker shown behind the "New" button. */
interface PickerTemplate {
  id: number;
  name: string;
  description: string | null;
  catalogVersionCode: string;
  isCurrentDefault: boolean;
}

/**
 * The two slices of the history grid.
 *
 * Archived reviews are history, not work in progress, so they get their own
 * sub-tab rather than sitting among the live rows where the status pill is the
 * only thing telling them apart. Superseded reviews stay in the active slice:
 * they were replaced, not archived, and still count on the chart above it.
 */
type HistoryTab = 'active' | 'archived';

/** Smiley bucket boundaries, matching the backend score formula. */
const YELLOW_FLOOR = 51;
const GREEN_FLOOR = 81;

@Component({
  selector: 'flexi-compliance-list',
  templateUrl: './compliance-list.component.html',
  styleUrls: ['./compliance-list.component.scss'],
  standalone: false
})
export class ComplianceListComponent implements OnInit {
  private readonly notifications = inject(NotificationService);

  reviews: FlexiComplianceSummary[] = [];
  loading = true;
  loadError: string | null = null;

  /* ─── Benchmark chart ─── */
  chartData: any = null;
  chartOptions: any = null;

  /**
   * The chart's series: one point per COMPLETION, oldest first, straight from
   * the backend's append-only score log.
   *
   * Deliberately not derived from `reviews`. A review row carries only its
   * LATEST score — re-opening a completed review and completing it again
   * overwrites that column — so a series built from the grid's rows replaced a
   * repeated assessment's earlier point rather than adding one, and a
   * progression like 7 % → 14 % was invisible.
   */
  scorePoints: FlexiComplianceScorePoint[] = [];

  /**
   * Mean of every recorded completion EXCEPT the latest — the baseline the most
   * recent score is read against. Null until two completions exist, since a
   * single result has nothing to be benchmarked against. Repeated assessments
   * count individually, so a re-run raises the bar the next one is read
   * against.
   */
  baseline: number | null = null;

  /* ─── "New review" template picker ─── */
  showStartNewDialog = false;
  pickerTemplates: PickerTemplate[] = [];
  pickerLoading = false;
  pickerError: string | null = null;
  selectedTemplateId: number | null = null;
  startingNewReview = false;

  /**
   * How many action points starting a new review will write off as "Avvist".
   *
   * Fetched when the dialog opens rather than reported afterwards: the sweep is
   * irreversible from the UI, and a commitment the customer still believes is
   * live disappearing without warning is the kind of thing people only notice
   * weeks later. Null while the count is still loading, or if the probe failed
   * — a number we are not sure of is worse than no number, so the warning is
   * simply omitted in that case and the review still starts.
   */
  outstandingActionPoints: number | null = null;
  outstandingLoading = false;

  readonly Status = FlexiComplianceStatus;

  /** Per-row in-flight latch so review lifecycle actions disable while saving. */
  busyReviewIds = new Set<number>();

  /* ─── Score explainer ─── */

  /** Whether the "how the score is calculated" dialog is open. */
  showScoreHelp = false;

  /**
   * The thresholds the explainer quotes, read from the same constants the
   * smiley buckets use rather than written into the copy.
   *
   * The authoritative switch is the backend's FlexiComplianceScoreCalculator;
   * these constants already mirror it, and hardcoding the numbers into four
   * locale files as well would make a third place for them to drift.
   */
  readonly scoreBands = {
    // The backend's bucket switch is `<= 50 red, <= 80 yellow, else green`,
    // so each band is stated from its own edge rather than the next band's.
    redCeiling: YELLOW_FLOOR - 1,
    yellowFloor: YELLOW_FLOOR,
    yellowCeiling: GREEN_FLOOR - 1,
    greenFloor: GREEN_FLOOR,
  };

  /* ─── History sub-tabs ─── */

  /** Which slice of the history grid is on screen. */
  historyTab: HistoryTab = 'active';

  /**
   * `reviews` split by status, recomputed on every change to the row set
   * rather than derived in a getter: p-table compares `[value]` by reference,
   * so an array rebuilt on each change-detection pass would reset the
   * paginator under the user on every keystroke elsewhere on the page.
   */
  activeReviews: FlexiComplianceSummary[] = [];
  archivedReviews: FlexiComplianceSummary[] = [];

  /**
   * One shared popup menu whose model is rebuilt for whichever row was
   * clicked — the same pattern the SDS/Chemicals grid uses, and the reason the
   * action column is a single kebab rather than a row of icon buttons.
   */
  @ViewChild('rowActionsMenu') rowActionsMenu?: Menu;
  actionMenuItems: MenuItem[] = [];

  constructor(
    private service: FlexiComplianceService,
    private templateService: FlexiComplianceTemplateService,
    private router: Router,
    private route: ActivatedRoute,
    private translate: TranslateService,
    private confirmationService: ConfirmationService
  ) {}

  get businessId(): number {
    return BaseServices.BusinessId;
  }

  /**
   * Whether this user may change anything here. False for a business-level
   * viewer, who gets the same grid and the same chart but no New, no Delete
   * and no Re-open — the scores read-only. The backend enforces this
   * independently: every write route is CSM-gated, and the rows a viewer is
   * given are the finished assessments only.
   */
  get canAuthor(): boolean {
    return isFlexiComplianceAuthor();
  }

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.loadError = null;
    forkJoin({
      reviews: this.service.listReviews(this.businessId),
      // The grid is the page; the chart is an extra on top of it. A timeline
      // that cannot be read should therefore cost the chart, not the whole
      // view — so this arm swallows its error and yields an empty series,
      // which hides the chart and leaves the rows intact.
      points: this.service.scoreTimeline(this.businessId).pipe(catchError(() => of([]))),
    }).subscribe({
      next: ({ reviews, points }) => {
        this.reviews = reviews;
        this.scorePoints = points;
        this.partitionReviews();
        this.buildChart();
        this.loading = false;
      },
      error: (err: HttpErrorResponse) => {
        this.loading = false;
        this.loadError = err?.error?.detail
          ?? this.translate.instant('FLEXI_COMPLIANCE.LIST.LOAD_FAILED');
      }
    });
  }

  // ==========================================================
  // History sub-tabs
  // ==========================================================

  /** Rows of the selected sub-tab — what the grid renders. */
  get visibleReviews(): FlexiComplianceSummary[] {
    return this.historyTab === 'archived' ? this.archivedReviews : this.activeReviews;
  }

  /**
   * Whether the sub-tab strip is offered at all.
   *
   * A business that has never archived a review keeps the plain single grid it
   * had before: a tab strip where one tab is permanently empty is a control
   * that only ever explains itself.
   */
  get hasArchivedReviews(): boolean {
    return this.archivedReviews.length > 0;
  }

  selectHistoryTab(tab: HistoryTab): void {
    this.historyTab = tab;
  }

  /**
   * Re-slice the grid whenever the row set changes.
   *
   * Restoring the last archived review empties the tab the user is standing on
   * AND removes the strip that would let them leave it, so the selection falls
   * back to the active rows whenever the archived slice runs out.
   */
  private partitionReviews(): void {
    this.activeReviews = this.reviews.filter(review => !this.isArchived(review));
    this.archivedReviews = this.reviews.filter(review => this.isArchived(review));
    if (this.historyTab === 'archived' && this.archivedReviews.length === 0) {
      this.historyTab = 'active';
    }
  }

  // ==========================================================
  // Benchmark chart
  // ==========================================================

  /** The chart needs at least one recorded completion to say anything. */
  get showChart(): boolean {
    return !this.loading && this.scorePoints.length > 0;
  }

  /**
   * Change between the latest score and the baseline, in percentage points.
   * Null while there is no baseline to compare against.
   */
  get baselineDelta(): number | null {
    const series = this.scorePoints;
    if (this.baseline == null || series.length === 0) return null;
    return round1(series[series.length - 1].percentage - this.baseline);
  }

  private buildChart(): void {
    const series = this.scorePoints;
    if (series.length === 0) {
      this.chartData = null;
      this.chartOptions = null;
      this.baseline = null;
      return;
    }

    const scores = series.map(p => p.percentage);
    const labels = buildPointLabels(series);
    const brand = brandRgb();

    // Baseline excludes the latest result so the newest score can be read as
    // above or below the business's own track record, not against itself.
    const previous = scores.slice(0, -1);
    this.baseline = previous.length
      ? round1(previous.reduce((sum, v) => sum + v, 0) / previous.length)
      : null;

    const datasets: any[] = [
      {
        label: this.translate.instant('FLEXI_COMPLIANCE.LIST.CHART_SCORE'),
        data: scores,
        borderColor: `rgb(${brand})`,
        backgroundColor: `rgba(${brand}, .12)`,
        pointBackgroundColor: scores.map(v => toneColour(v)),
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        // A re-run of the same assessment is drawn slightly larger, so the
        // repeated points read as a progression on one form rather than as
        // unrelated reviews that happen to sit next to each other.
        pointRadius: series.map(p => (p.isRepeat ? 6 : 5)),
        pointHoverRadius: series.map(p => (p.isRepeat ? 8 : 7)),
        pointStyle: series.map(p => (p.isRepeat ? 'rectRot' : 'circle')),
        borderWidth: 2,
        fill: true,
        tension: 0.3
      }
    ];

    if (this.baseline != null) {
      datasets.push({
        label: this.translate.instant('FLEXI_COMPLIANCE.LIST.CHART_BASELINE'),
        data: scores.map(() => this.baseline),
        borderColor: '#94a3b8',
        borderDash: [6, 4],
        borderWidth: 2,
        pointRadius: 0,
        pointHitRadius: 0,
        fill: false
      });
    }

    this.chartData = { labels, datasets };
    this.chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { position: 'bottom', labels: { usePointStyle: true, boxWidth: 8 } },
        tooltip: {
          callbacks: {
            label: (ctx: any) => `${ctx.dataset.label}: ${round1(ctx.parsed.y)}%`,
            // Which completion this point is, and how far it moved from the
            // one before it — the whole reason repeated assessments are kept
            // apart on the axis.
            afterBody: (items: any[]) => {
              const index: number = items[0]?.dataIndex ?? -1;
              const point = series[index];
              if (!point) return [];
              const lines: string[] = [];
              if (point.isRepeat) {
                lines.push(this.translate.instant(
                  'FLEXI_COMPLIANCE.LIST.CHART_RUN', { sequence: point.sequence }));
              }
              if (index > 0) {
                const change = round1(point.percentage - series[index - 1].percentage);
                lines.push(this.translate.instant(
                  'FLEXI_COMPLIANCE.LIST.CHART_VS_PREVIOUS',
                  { delta: `${change > 0 ? '+' : ''}${change}` }));
              }
              if (point.completedByName) {
                lines.push(`${this.translate.instant(
                  'FLEXI_COMPLIANCE.LIST.CHART_COMPLETED_BY')}: ${point.completedByName}`);
              }
              return lines;
            },
          },
        },
      },
      scales: {
        y: {
          min: 0,
          max: 100,
          ticks: { stepSize: 20, callback: (v: any) => `${v}%` },
          grid: { color: `rgba(${brand}, .09)` }
        },
        x: { grid: { display: false } }
      }
    };
  }

  // ==========================================================
  // Hero card — the latest published result
  // ==========================================================

  /**
   * The review the hero card reports.
   *
   * The business's live review once it has a score, falling back to the most
   * recent non-archived scored one — a freshly started review has no
   * percentage yet, and the hero must keep showing the result the customer is
   * actually living with rather than going blank the moment an advisor opens a
   * new one. That fallback is usually the review the new one superseded. An
   * archived review is out of both the chart and the live standing: the
   * backend's score timeline leaves archived reviews out. Null until the first
   * review has been completed, which hides the card.
   */
  get heroReview(): FlexiComplianceSummary | null {
    const current = this.reviews.find(r => r.isCurrent && r.percentage !== null);
    if (current) return current;
    // `reviews` arrives newest first, so the first match is the latest result.
    return this.reviews.find(r => (r.status === FlexiComplianceStatus.Completed
      || r.status === FlexiComplianceStatus.Superseded)
      && r.percentage !== null) ?? null;
  }

  /** Circumference of the hero ring (r = 52), shared by the dash maths. */
  readonly ringCircumference = 326.7;

  /** `stroke-dasharray` that fills the ring to the hero score. */
  get heroRingDash(): string {
    const pct = Math.max(0, Math.min(100, this.heroReview?.percentage ?? 0));
    return `${((pct / 100) * this.ringCircumference).toFixed(1)} ${this.ringCircumference}`;
  }

  /**
   * Smiley matching a review's bucket. Same three assets the General tab
   * uses, so one score never shows two different faces.
   */
  faceImage(review: FlexiComplianceSummary | null): string {
    switch (review?.smiley) {
      case 'green': return 'assets/images/happy-smile.png';
      case 'yellow': return 'assets/images/neutral-smile.png';
      case 'red': return 'assets/images/sad-smile.png';
      default: return '';
    }
  }

  /** Localized one-word summary next to the hero score. */
  labelKey(review: FlexiComplianceSummary | null): string {
    switch (review?.smiley) {
      case 'green': return 'FLEXI_COMPLIANCE.SCORE_CHIP.GREEN_LABEL';
      case 'yellow': return 'FLEXI_COMPLIANCE.SCORE_CHIP.YELLOW_LABEL';
      case 'red': return 'FLEXI_COMPLIANCE.SCORE_CHIP.RED_LABEL';
      default: return '';
    }
  }

  // ==========================================================
  // Row helpers
  // ==========================================================

  isCompleted(review: FlexiComplianceSummary): boolean {
    return review.status === FlexiComplianceStatus.Completed;
  }

  isArchived(review: FlexiComplianceSummary): boolean {
    return review.status === FlexiComplianceStatus.Archived;
  }

  isSuperseded(review: FlexiComplianceSummary): boolean {
    return review.status === FlexiComplianceStatus.Superseded;
  }

  statusKey(review: FlexiComplianceSummary): string {
    if (this.isArchived(review)) return 'FLEXI_COMPLIANCE.LIST.STATUS_ARCHIVED';
    if (this.isSuperseded(review)) return 'FLEXI_COMPLIANCE.LIST.STATUS_SUPERSEDED';
    if (this.isCompleted(review)) return 'FLEXI_COMPLIANCE.LIST.STATUS_COMPLETED';
    return 'FLEXI_COMPLIANCE.LIST.STATUS_OPEN';
  }

  statusClass(review: FlexiComplianceSummary): string {
    if (this.isArchived(review)) return 'is-archived';
    if (this.isSuperseded(review)) return 'is-superseded';
    if (this.isCompleted(review)) return 'is-completed';
    return 'is-open';
  }

  /**
   * Archived and superseded reviews are immutable snapshots — they open
   * read-only. So does every row for a viewer, who never re-opens or edits one.
   */
  actionKey(review: FlexiComplianceSummary): string {
    if (isFlexiComplianceSnapshot(review.status) || !this.canAuthor) return 'FLEXI_COMPLIANCE.LIST.VIEW';
    if (this.isCompleted(review)) return 'FLEXI_COMPLIANCE.LIST.REOPEN';
    return 'FLEXI_COMPLIANCE.LIST.EDIT';
  }

  actionIcon(review: FlexiComplianceSummary): string {
    if (isFlexiComplianceSnapshot(review.status) || !this.canAuthor) return 'pi-eye';
    if (this.isCompleted(review)) return 'pi-replay';
    return 'pi-pencil';
  }

  toneClass(review: FlexiComplianceSummary): string {
    return review.smiley ? `is-${review.smiley}` : 'is-none';
  }

  open(review: FlexiComplianceSummary): void {
    this.router.navigate([review.id], { relativeTo: this.route });
  }

  downloadPdf(review: FlexiComplianceSummary, event: Event): void {
    event.stopPropagation();
    this.service.downloadReportPdf(this.businessId, review.id).subscribe({
      next: blob => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = complianceReportFileName(
          this.translate.instant('FLEXI_COMPLIANCE.REPORT_FILE_NAME'),
          BaseServices.BusinessName,
          review.completedOn ?? review.createdOn,
        );
        link.click();
        URL.revokeObjectURL(url);
      },
      error: (err: HttpErrorResponse) => {
        this.loadError = err?.error?.detail
          ?? this.translate.instant('FLEXI_COMPLIANCE.LIST.PDF_FAILED');
      }
    });
  }

  // ==========================================================
  // Row actions menu
  // ==========================================================

  openActionsMenu(event: Event, review: FlexiComplianceSummary): void {
    event.stopPropagation();
    this.actionMenuItems = this.buildActionMenu(review);
    this.rowActionsMenu?.toggle(event);
  }

  /**
   * Actions for one row, ordered by intent rather than alphabetically: the
   * primary action first, then the document, then the destructive one last and
   * visually separated. Alphabetical order would put "Delete" at the top,
   * which is the wrong thing to have under the cursor by default.
   */
  private buildActionMenu(review: FlexiComplianceSummary): MenuItem[] {
    const items: MenuItem[] = [
      {
        // Edit / Re-open / View, depending on the review's state — archived
        // and superseded ones are immutable snapshots and open read-only.
        label: this.translate.instant(this.actionKey(review)),
        icon: `pi ${this.actionIcon(review)}`,
        command: () => this.open(review)
      }
    ];

    if (review.generatedFeatureManualDocumentId) {
      items.push({
        label: this.translate.instant('FLEXI_COMPLIANCE.LIST.DOWNLOAD_PDF'),
        icon: 'pi pi-file-pdf',
        command: () => this.downloadPdf(review, new Event('click'))
      });
    }

    if (this.canAuthor) {
      items.push({
        separator: true,
      });

      if (this.isArchived(review)) {
        items.push({
          label: this.translate.instant('FLEXI_COMPLIANCE.LIST.RESTORE'),
          icon: 'pi pi-undo',
          disabled: this.busyReviewIds.has(review.id),
          command: () => this.restoreReview(review, new Event('click')),
        });
      } else {
        items.push({
          label: this.translate.instant('FLEXI_COMPLIANCE.LIST.ARCHIVE'),
          icon: 'pi pi-folder',
          disabled: this.busyReviewIds.has(review.id),
          command: () => this.archiveReview(review, new Event('click')),
        });
      }

      items.push({
        label: this.translate.instant('FLEXI_COMPLIANCE.LIST.DELETE'),
        icon: 'pi pi-trash',
        styleClass: 'fcomp-action-danger',
        disabled: this.busyReviewIds.has(review.id),
        command: () => this.deleteReview(review, new Event('click')),
      });
    }

    return items;
  }

  // ==========================================================
  // Delete review
  // ==========================================================

  archiveReview(review: FlexiComplianceSummary, event: Event): void {
    event.stopPropagation();
    if (!this.canAuthor || this.busyReviewIds.has(review.id) || this.isArchived(review)) return;
    this.confirmationService.confirm({
      header: this.translate.instant('CONFIRMATION'),
      message: this.translate.instant('FLEXI_COMPLIANCE.CONFIRM.ARCHIVE_REVIEW'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translate.instant('SWITCH_YES'),
      rejectLabel: this.translate.instant('SWITCH_NO'),
      accept: () => {
        this.busyReviewIds.add(review.id);
        this.loadError = null;
        this.service.archiveReview(this.businessId, review.id).subscribe({
          next: () => {
            this.notifications.success('UI_NOTIFICATIONS.SAVED');
            this.busyReviewIds.delete(review.id);
            this.load();
          },
          error: (err: HttpErrorResponse) => {
            this.busyReviewIds.delete(review.id);
            this.loadError = err?.error?.detail
              ?? this.translate.instant('FLEXI_COMPLIANCE.LIST.ARCHIVE_FAILED');
          }
        });
      }
    });
  }

  restoreReview(review: FlexiComplianceSummary, event: Event): void {
    event.stopPropagation();
    if (!this.canAuthor || this.busyReviewIds.has(review.id) || !this.isArchived(review)) return;
    this.confirmationService.confirm({
      header: this.translate.instant('CONFIRMATION'),
      message: this.translate.instant('FLEXI_COMPLIANCE.CONFIRM.RESTORE_REVIEW'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translate.instant('SWITCH_YES'),
      rejectLabel: this.translate.instant('SWITCH_NO'),
      accept: () => {
        this.busyReviewIds.add(review.id);
        this.loadError = null;
        this.service.restoreReview(this.businessId, review.id).subscribe({
          next: () => {
            this.notifications.success('UI_NOTIFICATIONS.SAVED');
            this.busyReviewIds.delete(review.id);
            this.load();
          },
          error: (err: HttpErrorResponse) => {
            this.busyReviewIds.delete(review.id);
            this.loadError = err?.error?.detail
              ?? this.translate.instant('FLEXI_COMPLIANCE.LIST.RESTORE_FAILED');
          }
        });
      }
    });
  }

  /**
   * Soft-deletes a whole review. Every row here belongs to the business and
   * the action is only offered to an author (and refused server-side to
   * anyone else), so there is no per-row condition — completed and archived
   * reviews go too,
   * and the cascade takes ALL of their action points with them, including the
   * completed ones that the per-action-point delete refuses to remove.
   *
   * The list is re-fetched rather than patched locally, the way archive and
   * restore already do. The benchmark chart is drawn from the score timeline,
   * which the backend filters to the active lineage, so a deleted review's
   * points have to go with its row — a local splice left them on the chart
   * until the next page load.
   */
  deleteReview(review: FlexiComplianceSummary, event: Event): void {
    event.stopPropagation();
    if (!this.canAuthor || this.busyReviewIds.has(review.id)) return;
    this.confirmationService.confirm({
      header: this.translate.instant('CONFIRMATION'),
      message: this.translate.instant('FLEXI_COMPLIANCE.CONFIRM.DELETE_REVIEW'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translate.instant('SWITCH_YES'),
      rejectLabel: this.translate.instant('SWITCH_NO'),
      accept: () => {
        this.busyReviewIds.add(review.id);
        this.loadError = null;
        this.service.deleteReview(this.businessId, review.id).subscribe({
          next: () => {
            this.notifications.success('UI_NOTIFICATIONS.DELETED');
            this.busyReviewIds.delete(review.id);
            this.load();
          },
          error: (err: HttpErrorResponse) => {
            this.busyReviewIds.delete(review.id);
            this.loadError = err?.error?.detail
              ?? this.translate.instant('FLEXI_COMPLIANCE.LIST.DELETE_FAILED');
          }
        });
      }
    });
  }

  // ==========================================================
  // New review
  // ==========================================================

  /**
   * Starting a review supersedes the current one, so the template choice is
   * made up front rather than defaulted — same picker the rating view uses.
   */
  openStartNewReview(): void {
    if (!this.canAuthor) return;
    this.showStartNewDialog = true;
    this.pickerError = null;
    this.selectedTemplateId = this.reviews.find(r => r.isCurrent)?.templateId ?? null;
    this.loadPickerTemplates();
    this.loadOutstandingActionPoints();
  }

  /**
   * Counts the commitments the sweep will reject, for the dialog's warning.
   *
   * A failure here is swallowed on purpose: this is an advisory number, and
   * blocking someone from starting a review because a count could not be read
   * would be a worse outcome than starting one without the warning.
   */
  private loadOutstandingActionPoints(): void {
    this.outstandingActionPoints = null;
    this.outstandingLoading = true;
    this.service.countOutstandingActionPoints(this.businessId).subscribe({
      next: count => {
        this.outstandingActionPoints = count;
        this.outstandingLoading = false;
      },
      error: () => {
        this.outstandingActionPoints = null;
        this.outstandingLoading = false;
      }
    });
  }

  closeStartNewReview(): void {
    if (this.startingNewReview) return;
    this.showStartNewDialog = false;
  }

  goCreateTemplate(): void {
    this.showStartNewDialog = false;
    this.router.navigate(['templates', 'new'], { relativeTo: this.route });
  }

  confirmStartNewReview(): void {
    if (this.startingNewReview || this.selectedTemplateId == null) return;
    this.startingNewReview = true;
    this.pickerError = null;
    this.service.startNewReview(this.businessId, this.selectedTemplateId).subscribe({
      next: aggregate => {
        this.startingNewReview = false;
        this.showStartNewDialog = false;
        // Straight into the new review — it is the live one by definition.
        this.router.navigate([aggregate.id], { relativeTo: this.route });
      },
      error: (err: HttpErrorResponse) => {
        this.startingNewReview = false;
        this.pickerError = err.error?.detail
          ?? this.translate.instant('FLEXI_COMPLIANCE.START_NEW.FAILED');
      }
    });
  }

  private loadPickerTemplates(): void {
    this.pickerLoading = true;
    const currentDefaultId = this.reviews.find(r => r.isCurrent)?.templateId ?? null;
    // Scoped to this business: its own templates plus unclaimed library ones.
    // An unscoped list showed every business the first customer's
    // "HMS-status — <their name>" and, because the list then looked
    // populated, suppressed the ensure-default that would have created this
    // business's own.
    this.templateService.list(false, this.businessId).subscribe({
      next: rows => {
        this.pickerTemplates = (rows || [])
          .filter(r => r.isActive && !r.isDeleted)
          .map(r => ({
            id: r.id,
            name: r.name,
            description: r.description ?? null,
            catalogVersionCode: r.catalogVersionCode,
            isCurrentDefault: r.id === currentDefaultId
          }))
          // Current default first so the CSM doesn't have to hunt for it.
          .sort((a, b) => {
            if (a.isCurrentDefault && !b.isCurrentDefault) return -1;
            if (b.isCurrentDefault && !a.isCurrentDefault) return 1;
            return a.name.localeCompare(b.name);
          });

        // An empty library is a dead end, not an empty state: confirmStartNewReview
        // refuses to run without a selectedTemplateId, so the dialog would offer
        // nothing to pick and the button would sit inert with no explanation. That
        // is the normal situation for a business whose compliance tab was just
        // enabled — templates are created lazily and nothing has created one yet.
        if (this.pickerTemplates.length === 0) {
          this.ensureTemplateForEmptyPicker();
          return;
        }

        this.pickerLoading = false;
        this.preselectPickerTemplate();
      },
      error: (err: HttpErrorResponse) => {
        this.pickerLoading = false;
        this.pickerError = err.error?.detail
          ?? this.translate.instant('FLEXI_COMPLIANCE.START_NEW.LOAD_FAILED');
      }
    });
  }

  /**
   * Creates the business's default template when the library is empty, then
   * puts it in the picker and selects it. Same call the rating page's
   * Edit-template button makes, and idempotent — a second open finds the
   * existing template rather than making another.
   */
  private ensureTemplateForEmptyPicker(): void {
    this.templateService.ensureDefaultForBusiness(this.businessId).subscribe({
      next: template => {
        this.pickerTemplates = [{
          id: template.id,
          name: template.name,
          description: template.description ?? null,
          catalogVersionCode: template.catalogVersionCode,
          isCurrentDefault: true
        }];
        this.pickerLoading = false;
        this.preselectPickerTemplate();
      },
      error: (err: HttpErrorResponse) => {
        this.pickerLoading = false;
        this.pickerError = err.error?.detail
          ?? this.translate.instant('FLEXI_COMPLIANCE.START_NEW.LOAD_FAILED');
      }
    });
  }

  /**
   * Makes sure something is selected once the list is known. The selection is
   * seeded from the current review's templateId, which is null for every review
   * seeded straight from the catalogue — so without this the dialog opens with a
   * populated list, nothing chosen, and a confirm button that does nothing.
   */
  private preselectPickerTemplate(): void {
    const stillListed = this.selectedTemplateId !== null
      && this.pickerTemplates.some(t => t.id === this.selectedTemplateId);
    if (stillListed) return;

    const preferred = this.pickerTemplates.find(t => t.isCurrentDefault)
      ?? this.pickerTemplates[0];
    this.selectedTemplateId = preferred ? preferred.id : null;
  }
}

function round1(value: number): number {
  return Math.round(value * 10) / 10;
}

/**
 * X-axis label per point.
 *
 * A date alone is ambiguous the moment two completions land on the same day —
 * which is exactly what a re-run of the same assessment tends to look like, and
 * two identical labels read as one point drawn twice. So a date shared by more
 * than one point carries its time as well; a date that is unique stays short.
 */
function buildPointLabels(series: FlexiComplianceScorePoint[]): string[] {
  const dates = series.map(p => new Date(p.completedOn));
  const dayLabels = dates.map(d => formatDay(d));

  const perDay = new Map<string, number>();
  dayLabels.forEach(label => perDay.set(label, (perDay.get(label) ?? 0) + 1));

  return dayLabels.map((label, i) => {
    if ((perDay.get(label) ?? 0) < 2) return label;
    return `${label} ${formatTime(dates[i])}`;
  });
}

const pad2 = (n: number): string => String(n).padStart(2, '0');

/**
 * dd-MM-yyyy, written out rather than left to toLocaleDateString(): with no
 * locale argument that follows the BROWSER's locale, so the same chart read
 * 09.09.2026 on one machine and 9/9/2026 on another, neither of which is the
 * format the rest of the app uses.
 */
function formatDay(d: Date): string {
  return `${pad2(d.getDate())}-${pad2(d.getMonth() + 1)}-${d.getFullYear()}`;
}

/** HH:mm, 24-hour — same reason as formatDay, which otherwise gave AM/PM. */
function formatTime(d: Date): string {
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/**
 * The tenant's brand colour as "r, g, b", ready to interpolate into rgb()/rgba().
 *
 * Chart.js paints to a canvas and parses colours itself, so it understands
 * neither `var(--color-primary-500)` nor `color-mix()` — the chart is the one
 * place in this feature that cannot simply reference the token. Reading the
 * computed property keeps it following the theme anyway, so an Agil business
 * gets a teal chart rather than a purple one on a teal page.
 *
 * The fallback is Kuba's purple, used only if the theme stylesheet has not been
 * applied yet; the chart is redrawn on the next render.
 */
function brandRgb(): string {
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue('--color-primary-500')
    .trim();

  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(raw);
  if (hex) {
    const h =
      hex[1].length === 3
        ? hex[1]
            .split('')
            .map(c => c + c)
            .join('')
        : hex[1];
    return [0, 2, 4].map(i => parseInt(h.slice(i, i + 2), 16)).join(', ');
  }

  // Sass emits the scaled shades as rgb(18, 110.7, 112.5); round the channels.
  const rgb = /^rgba?\(([^)]+)\)$/i.exec(raw);
  if (rgb) {
    return rgb[1]
      .split(',')
      .slice(0, 3)
      .map(v => Math.round(parseFloat(v)))
      .join(', ');
  }

  return '145, 38, 143';
}

/** Point colour by smiley bucket, matching the backend thresholds. */
function toneColour(percentage: number): string {
  if (percentage >= GREEN_FLOOR) return '#15803d';
  if (percentage >= YELLOW_FLOOR) return '#b45309';
  return '#b91c1c';
}
